import { User } from "@hooks/useGetUser";
import { languageDutch, languageEnglish } from "../i18n";
import { isStoryTranslated } from "./isStoryTranslated";

interface TranslatableStory {
  translated_slugs?: { lang: string; name?: string | null }[] | null;
}

interface IsStoryAutoTranslatedOptions {
  user: User | null | undefined;
  story: TranslatableStory;
  lang: string | undefined;
}

/**
 * Returns true when the story is being auto-translated for the current user.
 *
 * A story is considered auto-translated when ALL of the following are true:
 * - user.autoTranslateEnabled === true
 * - user.autoTranslateLanguage is set
 * - The chosen autoTranslateLanguage differs from the story's native language
 *   for the current route:
 *   - On NL route (/): native is "nl" → auto-translated when autoTranslateLanguage !== "nl"
 *   - On EN route (/en/...): if story has an EN translation, native is "en" →
 *     auto-translated when autoTranslateLanguage !== "en". If the story has NO EN
 *     translation, the NL source is being translated, so it is auto-translated
 *     regardless of the autoTranslateLanguage value (since the user is browsing EN
 *     and the content is always NL-source).
 */
export const isStoryAutoTranslated = ({
  user,
  story,
  lang,
}: IsStoryAutoTranslatedOptions): boolean => {
  if (!user?.autoTranslateEnabled || !user?.autoTranslateLanguage) {
    return false;
  }

  const autoTranslateLanguage = user.autoTranslateLanguage;

  if (lang === languageEnglish) {
    if (isStoryTranslated(story)) {
      // Story has a native EN translation — only auto-translated when the chosen
      // language differs from EN.
      return autoTranslateLanguage !== languageEnglish;
    }
    // Story has no EN translation — the NL source is always being translated.
    return true;
  }

  // NL route — native language is NL.
  return autoTranslateLanguage !== languageDutch;
};
